import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import './SchoolDashboard.css';

function CardGeneration() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [sections, setSections] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [showCards, setShowCards] = useState(false);

  useEffect(() => {
    // Get school data from localStorage
    const storedSchoolData = localStorage.getItem('schoolData');
    if (storedSchoolData) {
      setSchoolData(JSON.parse(storedSchoolData));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (schoolData?.id) {
      fetchClasses();
      fetchStudents();
    }
  }, [schoolData?.id]);

  useEffect(() => {
    if (selectedClass) {
      fetchSections();
    }
  }, [selectedClass]);

  useEffect(() => {
    if (selectedClass && selectedSection) {
      filterStudents();
    }
  }, [selectedClass, selectedSection, students]);

  const fetchClasses = async () => {
    try {
      const { data, error } = await supabase
        .from('classes')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('is_active', true)
        .order('class_name');

      if (error) {
        console.error('Error fetching classes:', error);
        return;
      }

      setClasses(data || []);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchStudents = async () => {
    try {
      setLoadingStudents(true);
      const { data, error } = await supabase
        .from('students')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('is_active', true)
        .order('first_name');

      if (error) {
        console.error('Error fetching students:', error);
        return;
      }

      setStudents(data || []);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoadingStudents(false);
    }
  };

  const fetchSections = async () => {
    if (!selectedClass) return;

    try {
      const selectedClassData = classes.find(c => c.id === parseInt(selectedClass));
      if (selectedClassData && selectedClassData.sections) {
        const sectionObjects = selectedClassData.sections
          .filter(section => section && section.trim() !== '')
          .map((section, index) => ({
            id: index + 1,
            section_name: section,
            class_id: selectedClassData.id
          }));

        setSections(sectionObjects);
      } else {
        setSections([]);
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const filterStudents = () => {
    if (!selectedClass || !selectedSection) {
      setFilteredStudents([]);
      return;
    }

    const selectedClassData = classes.find(c => c.id === parseInt(selectedClass));
    const selectedSectionData = sections.find(s => s.id === parseInt(selectedSection));

    if (!selectedClassData || !selectedSectionData) {
      setFilteredStudents([]);
      return;
    }

    const filtered = students.filter(student => {
      const classMatch = student.class_id === parseInt(selectedClass);
      const sectionMatch = student.section_id === selectedSectionData.section_name;
      
      return classMatch && sectionMatch;
    });

    setFilteredStudents(filtered);
  };

  const handleClassChange = (e) => {
    setSelectedClass(e.target.value);
    setSelectedSection('');
    setSections([]);
    setFilteredStudents([]);
    setShowCards(false);
  };

  const handleSectionChange = (e) => {
    setSelectedSection(e.target.value);
    setShowCards(false);
  };

  const handleGenerateCards = () => {
    if (filteredStudents.length > 0) {
      setShowCards(true);
    }
  };

  const handlePrintCards = () => {
    window.print();
  };


  const getDefaultAvatar = (name) => {
    const initials = name.split(' ').map(n => n[0]).join('').toUpperCase();
    return `https://ui-avatars.com/api/?name=${initials}&background=667eea&color=fff&size=140&bold=true&format=png`;
  };

  const getProfilePicture = (student) => {
    console.log('Student data:', student);
    console.log('Profile picture URL:', student.profile_picture_url);
    
    // If student has a profile picture URL, use it
    if (student.profile_picture_url && student.profile_picture_url.trim() !== '') {
      console.log('Using profile picture URL:', student.profile_picture_url);
      return student.profile_picture_url;
    }
    
    // Otherwise, create a nice avatar with the student's name
    const fullName = `${student.first_name} ${student.last_name}`;
    const avatarUrl = getDefaultAvatar(fullName);
    console.log('Using default avatar:', avatarUrl);
    return avatarUrl;
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'N/A';
    // Simple phone formatting
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const handleLogout = () => {
    navigate('/schoollogin');
  };

  if (loading) {
    return (
      <div className="school-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!schoolData) {
    return (
      <div className="school-dashboard">
        <div className="error-container">
          <h2>Access Denied</h2>
          <p>Please log in to access the school dashboard.</p>
          <button onClick={() => navigate('/schoollogin')} className="btn-primary">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />
      
      <div className="main-content">
        <div className="content-header">
          <h1>🆔 Card Generation</h1>
          <p>Generate student ID cards for classes and sections</p>
        </div>

        <div className="card-generation-container">
          {/* Selection Section */}
          <div className="selection-section">
            <div className="selection-card">
              <h2>📋 Select Class and Section</h2>
              
              <div className="selection-form">
                <div className="form-group">
                  <label htmlFor="class-select">Class *</label>
                  <select
                    id="class-select"
                    value={selectedClass}
                    onChange={handleClassChange}
                    className="form-select"
                  >
                    <option value="">Select a class</option>
                    {classes.map(cls => (
                      <option key={cls.id} value={cls.id}>
                        {cls.class_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="section-select">Section *</label>
                  <select
                    id="section-select"
                    value={selectedSection}
                    onChange={handleSectionChange}
                    className="form-select"
                    disabled={!selectedClass}
                  >
                    <option value="">Select a section</option>
                    {sections.map(section => (
                      <option key={section.id} value={section.id}>
                        {section.section_name}
                      </option>
                    ))}
                  </select>
                </div>

                {filteredStudents.length > 0 && (
                  <div className="students-count">
                    <span className="count-badge">
                      {filteredStudents.length} student{filteredStudents.length !== 1 ? 's' : ''} found
                    </span>
                  </div>
                )}

                <div className="form-actions">
                  <button
                    onClick={handleGenerateCards}
                    disabled={!selectedClass || !selectedSection || filteredStudents.length === 0}
                    className="generate-btn"
                  >
                    🆔 Generate Cards
                  </button>
                  
                  <button
                    onClick={() => {
                      setFilteredStudents(students);
                      setShowCards(true);
                    }}
                    disabled={students.length === 0}
                    className="show-all-btn"
                  >
                    👥 Show All Students
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Cards Display Section */}
          {showCards && filteredStudents.length > 0 && (
            <div className="cards-display-section">
              <div className="cards-header">
                <h2>🆔 Student ID Cards</h2>
                <div className="cards-info">
                  <span className="class-info">
                    {classes.find(c => c.id === parseInt(selectedClass))?.class_name} - 
                    {sections.find(s => s.id === parseInt(selectedSection))?.section_name}
                  </span>
                  <button onClick={handlePrintCards} className="print-btn">
                    🖨️ Print Cards
                  </button>
                </div>
              </div>

              <div className="cards-grid">
                 {filteredStudents.map((student, index) => (
                   <div key={student.id} className="modern-student-card">
                     {/* Card Header */}
                     <div className="modern-card-header">
                       <div className="school-logo-section">
                         <div className="school-logo-circle">
                           <span className="school-initials">
                             {schoolData.school_name.split(' ').map(word => word[0]).join('').slice(0, 2)}
                           </span>
                         </div>
                       </div>
                       <div className="card-title-section">
                         <h3 className="card-main-title">STUDENT ID CARD</h3>
                         <p className="card-subtitle">{schoolData.school_name}</p>
                       </div>
                     </div>

                     {/* Card Body */}
                     <div className="modern-card-body">
                       {/* Student Photo Section */}
                       <div className="photo-section">
                         <div className="photo-container">
                           <img
                             src={getProfilePicture(student)}
                             alt={`${student.first_name} ${student.last_name}`}
                             className="student-photo"
                             onError={(e) => {
                               e.target.src = getDefaultAvatar(`${student.first_name} ${student.last_name}`);
                             }}
                           />
                           <div className="photo-border"></div>
                         </div>
                       </div>

                       {/* Student Information */}
                       <div className="info-section">
                         <div className="info-grid">
                           <div className="info-item">
                             <span className="info-label">FULL NAME</span>
                             <span className="info-value">{student.first_name} {student.last_name}</span>
                           </div>
                           
                           <div className="info-item">
                             <span className="info-label">CLASS & SECTION</span>
                             <span className="info-value">
                               {classes.find(c => c.id === parseInt(selectedClass))?.class_name} - 
                               {sections.find(s => s.id === parseInt(selectedSection))?.section_name}
                             </span>
                           </div>
                           
                           <div className="info-item">
                             <span className="info-label">ROLL NUMBER</span>
                             <span className="info-value">{student.roll_number || student.id}</span>
                           </div>
                           
                           <div className="info-item">
                             <span className="info-label">CONTACT</span>
                             <span className="info-value">{formatPhoneNumber(student.parent_phone)}</span>
                           </div>
                         </div>
                       </div>
                     </div>

                     {/* Card Footer */}
                     <div className="modern-card-footer">
                       <div className="footer-left">
                         <div className="validity-text">Valid Until: 2025-2026</div>
                       </div>
                       <div className="footer-right">
                         <div className="student-id-badge">
                           <span className="id-label">ID</span>
                           <span className="id-number">{student.id}</span>
                         </div>
                       </div>
                     </div>

                     {/* Decorative Elements */}
                     <div className="card-decoration">
                       <div className="decoration-circle circle-1"></div>
                       <div className="decoration-circle circle-2"></div>
                       <div className="decoration-line"></div>
                     </div>
                  </div>
                ))}
              </div>
            </div>
          )}


          {/* No Students Message - Only show when no students are found */}
          {selectedClass && selectedSection && filteredStudents.length === 0 && !loadingStudents && students.length > 0 && (
            <div className="no-students-section">
              <div className="no-students-card">
                <div className="no-students-icon">👥</div>
                <h3>No Students Found</h3>
                <p>No students found for the selected class and section.</p>
                <p>Check the debug information above to see the actual student data.</p>
                <button 
                  onClick={() => {
                    setFilteredStudents(students);
                    setShowCards(true);
                  }}
                  className="show-all-btn"
                >
                  👥 Show All Students Anyway
                </button>
              </div>
            </div>
          )}

          {/* Loading Students */}
          {loadingStudents && (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading students...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CardGeneration;
